/**
 * Flow family: sankey.
 *
 * Nodes are derived from both endpoints of every link and deduplicated in
 * first-seen order, so colour assignment is stable across renders rather than
 * shifting when a link is added.
 */
import type { EChartsOption } from 'echarts';
import { chartTokens, type ChartMedia, type ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartRow } from '../types';
import { baseOption } from './shared';

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function buildFlowOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  const tokens = chartTokens[mode];
  const entry = config.series.find((s) => !s.hidden);
  const sourceField = entry?.fields.x;
  const targetField = entry?.fields.target;
  const valueField = entry?.fields.value;

  const usable =
    entry &&
    sourceField &&
    targetField &&
    valueField &&
    data.length > 0 &&
    sourceField in data[0] &&
    targetField in data[0] &&
    valueField in data[0];

  if (!usable) {
    if (entry && data.length > 0) {
      console.warn(
        `[octopus-ui/chart] sankey series "${entry.name}" references fields not present in ` +
          'the data; nothing to render.',
      );
    }
    return { ...baseOption(config, mode, media), series: [] } as EChartsOption;
  }

  const names: string[] = [];
  for (const row of data) {
    for (const field of [sourceField, targetField]) {
      const name = String(row[field]);
      if (!names.includes(name)) names.push(name);
    }
  }

  return {
    ...baseOption(config, mode, media),
    series: [
      {
        id: entry.id,
        name: entry.name,
        type: 'sankey',
        emphasis: { focus: 'adjacency' },
        label: { color: tokens.ink.label },
        lineStyle: { color: 'gradient', opacity: 0.35 },
        data: names.map((name, index) => ({
          name,
          itemStyle: { color: tokens.series[index % tokens.series.length] },
        })),
        links: data.map((row) => ({
          source: String(row[sourceField]),
          target: String(row[targetField]),
          value: toNumber(row[valueField]),
        })),
      },
    ],
  } as EChartsOption;
}
